import { Button, CheckIcon, ProfilePhoto, Text } from "@minervaproject/fds";
import cuid from "cuid";
import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";

import { PollConfirmationTextAndEditButton } from "./poll_response_form_parts";
import { PollVisibility } from "./poll_visibility";

import { FlightAPIContext } from "contexts/flight_api_context";
import { useAppDispatch, useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { useInterval } from "hooks/use_interval";
import { useIsMobile } from "hooks/use_is_mobile";
import { useUserProfileProperties } from "hooks/use_user_properties";
import { createOrUpdatePollResponse } from "shared/reducers/classroom";
import { PollSessionStatusType, PollSessionType, PollType } from "shared/types";
import { classroomSelectors, localSelectors } from "store";
import { deleteInProgressPollResponse, saveInProgressPollResponse } from "utils/poll";
import { computeInitials, longDisplayName, profilePhotoUrl } from "utils/user";

const MAX_RESPONSE_LENGTH = 600;
const AUTO_SAVE_INTERVAL = 15 * 1000;

type LongFormPollResponseFormProps = {
  poll: PollType;
  pollSession: PollSessionType;
  userId: string;
};

function LongFormPollResponseForm({ poll, pollSession, userId }: LongFormPollResponseFormProps) {
  const dispatch = useAppDispatch();
  const flightAPI = useContext(FlightAPIContext);
  const pollResponses = useArraySelector(
    classroomSelectors.selectPollResponsesByPollSessionIdAndUserId(pollSession.id, userId),
  );
  const user = useUserProfileProperties(userId);
  const joinTimestamp = useAppSelector(localSelectors.selectJoinTimestamp());
  const pollResponse = pollResponses.length ? pollResponses[0] : null;
  const isDraft = pollResponse ? pollResponse.is_draft : true;
  const responseText = useMemo(() => {
    const inProgressPollResponses = JSON.parse(
      localStorage.getItem("poll-responses-in-progress") || "{}",
    );
    const inProgressPollResponse = inProgressPollResponses[pollSession.id];
    return !isDraft ? (pollResponse?.response as string) : inProgressPollResponse || "";
  }, [isDraft, pollResponse?.response, pollSession.id]);
  const [draftResponse, setDraftResponse] = useState(responseText);
  const isMobile = useIsMobile();
  const [isFinalSaveComplete, setIsFinalSaveComplete] = useState(false);
  // Every save from this form reuses the same id, so a save that is re-sent (for example after
  // the previous one was lost in a reconnect) updates the response instead of creating a second one
  const initialCuid = useMemo(() => cuid(), []);

  const handleSubmit = useCallback(
    (isAutoSave = false) => {
      const trimmedResponse = draftResponse.slice(0, MAX_RESPONSE_LENGTH);
      // Skip an autosave only while the client state already holds this draft: if a reconnect
      // dropped the last save, the next autosave sends it again even when nothing was typed
      if (
        isAutoSave &&
        (trimmedResponse.length === 0 || pollResponse?.response === trimmedResponse)
      )
        return;

      window.flightLogger.log(
        `[LongFormPollResponseForm] saving response isAutoSave=${isAutoSave} response=${trimmedResponse}`,
      );
      dispatch(
        createOrUpdatePollResponse({
          id: pollResponse?.id || initialCuid,
          pollSessionId: pollSession.id,
          userId,
          response: trimmedResponse || "This student was present, but did not complete the poll.",
          isDraft: isAutoSave,
          submittedAt: !isAutoSave ? flightAPI.getServerMilliseconds() : null,
          hideFromStudents:
            !poll.student_visible || pollSession.status === PollSessionStatusType.InProgress
              ? ["response"]
              : [],
        }),
      );
    },
    [
      dispatch,
      draftResponse,
      poll.student_visible,
      pollResponse?.id,
      pollResponse?.response,
      pollSession.id,
      pollSession.status,
      flightAPI,
      userId,
      initialCuid,
    ],
  );

  useEffect(() => {
    // Fixes bug where a poll response is saved for a student who joins class after the poll has ended
    if (!joinTimestamp || (pollSession.ended_at && joinTimestamp > pollSession.ended_at)) {
      return;
    }

    if (pollSession.status === PollSessionStatusType.Completed && isDraft && !isFinalSaveComplete) {
      window.flightLogger.log("[LongFormPollResponseForm] final save");
      handleSubmit();
      setIsFinalSaveComplete(true);
    }
  }, [
    handleSubmit,
    isDraft,
    isFinalSaveComplete,
    joinTimestamp,
    pollSession.ended_at,
    pollSession.status,
  ]);

  // Once the poll has ended, only the final save above may store a response
  useInterval(() => {
    if (isDraft && pollSession.status === PollSessionStatusType.InProgress) {
      handleSubmit(true);
    }
  }, AUTO_SAVE_INTERVAL);

  useEffect(() => {
    const _saveInProgressPollResponse = () => {
      if (pollSession.status === PollSessionStatusType.InProgress && draftResponse.length > 0) {
        saveInProgressPollResponse(pollSession.id, draftResponse);
      }
    };
    window.addEventListener("pagehide", _saveInProgressPollResponse);

    return () => {
      if (pollSession.status === PollSessionStatusType.InProgress && draftResponse.length > 0) {
        saveInProgressPollResponse(pollSession.id, draftResponse);
      } else if (pollResponse && pollSession.status === PollSessionStatusType.Completed) {
        deleteInProgressPollResponse(pollSession.id);
      }
      window.removeEventListener("pagehide", _saveInProgressPollResponse);
    };
  }, [draftResponse, pollResponse, pollSession.id, pollSession.status]);

  const handleChangeResponse = (e: ChangeEvent) =>
    setDraftResponse((e.target as HTMLInputElement).value);

  const handleEdit = () => {
    if (!pollResponse?.id) {
      window.flightLogger.error(
        "[LongFormPollResponseForm] trying to edit a poll response without an id",
      );
      return;
    }
    dispatch(
      createOrUpdatePollResponse({
        id: pollResponse.id,
        isDraft: true,
        submittedAt: null,
      }),
    );
  };

  if (pollSession.status === PollSessionStatusType.Completed) return null;

  const charactersRemainingInDraftResponse = MAX_RESPONSE_LENGTH - draftResponse.length;
  const hasCharactersRemainingInDraftResponse = charactersRemainingInDraftResponse > 0;

  return isDraft ? (
    <form>
      <div className="relative mb3">
        <TextareaAutosize
          minRows={3}
          className="body pt2 pb6 px4 mb4 w-full block outline-none border-box"
          value={draftResponse}
          id="response"
          maxLength={MAX_RESPONSE_LENGTH}
          style={{
            resize: isMobile ? "none" : "vertical",
            backgroundColor: isMobile ? "white" : "var(--blue-tint-90)",
            border: hasCharactersRemainingInDraftResponse ? "none" : "1px solid red",
            borderRadius: isMobile ? "2px" : "5px",
          }}
          onChange={handleChangeResponse}
        />
        {draftResponse.length > 0 && draftResponse === pollResponse?.response && (
          <div className="flex items-center absolute" style={{ bottom: "0.5rem", left: "0.5rem" }}>
            <CheckIcon color="green" mr={1} />
            <Text kind="body-xs" color="blackTint40">
              Changes saved
            </Text>
          </div>
        )}
        <Text
          kind="body-xs"
          color={hasCharactersRemainingInDraftResponse ? "blackTint40" : "red"}
          className="absolute"
          style={{ bottom: "0.5rem", right: "0.5rem" }}
        >
          {charactersRemainingInDraftResponse} characters remaining
        </Text>
      </div>
      {isMobile ? (
        <Button
          kind="primary-mobile"
          className="w-full"
          data-testid="submit-poll-button"
          onClick={() => handleSubmit(false)}
          disabled={draftResponse.length === 0}
        >
          Submit
        </Button>
      ) : (
        <>
          <PollVisibility isStudentVisible={poll.student_visible} />
          <Button
            className="mt2 mb4"
            onClick={() => handleSubmit(false)}
            disabled={draftResponse.length === 0}
            data-testid="submit-poll-button"
          >
            Submit
          </Button>
        </>
      )}
    </form>
  ) : (
    <>
      {isMobile && (
        <div className="flex items-center mb2">
          <ProfilePhoto
            className="mr3"
            size={isMobile ? "md" : "lg"}
            src={profilePhotoUrl(user)}
            initials={computeInitials(user)}
          />
          <Text kind="body-s" bold color="blackTint40" className="flex-none">
            {longDisplayName(user)}
          </Text>
        </div>
      )}
      <pre
        className="wrap-words mb4"
        style={{ whiteSpace: "pre-wrap" }}
        data-testid="poll-response"
      >
        {responseText}
      </pre>
      <PollConfirmationTextAndEditButton isMobile={isMobile} handleEdit={handleEdit} />
    </>
  );
}

export { LongFormPollResponseForm };
